import * as React from 'react';
import propz from 'propz';
import BPromise from 'bluebird';
import moment from 'moment';
import Lazy from 'lazy.js';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2, SelectOptions } from 'Src/components/Grid/Grid2';
import { faSchool } from '@fortawesome/free-solid-svg-icons';
import { Loader } from 'Src/components/Loader/Loader';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import { AGE_GROUPS } from 'Src/consts/school';
import { SchoolForm } from 'Src/models/form';
import {
  deleteSchoolUnionSchool,
  getAllSchoolUnionSchools,
  getSchoolUnionSchools,
  getSchoolUnionSchoolsCount,
  getUpcomingTournaments,
  setSchoolsUnionWebsiteVisibility
} from 'Src/helpers/service/adminSU/schoolsTable';
import {
  getSchoolUnionSchoolStudents,
  getSchoolUnionSchoolForms
} from 'Src/helpers/service/adminSU/schoolStudentsTable';
import { sortAges } from 'Src/helpers/event/event';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/report';
import { SCHOOL_TABS } from 'Src/consts/school';
import { SchoolSummary } from 'Src/components/SchoolSummary/SchoolSummary';
import { SchoolUpcomingTournamentsSummary } from 'Src/components/SchoolUpcomingTournamentsSummary/SchoolUpcomingTournamentsSummary';
import { UpcomingTournament } from 'Src/models/upcomingTournament';
import { SchoolAdminsSummary } from 'Src/components/SchoolAdminsSummary/SchoolAdminsSummary';
import { Button } from 'Src/components/Button/Button';
import { getAllSchoolAdmins } from 'Src/helpers/service/adminSU/schoolAdminsTable';
import { uploadReport } from 'Src/helpers/service/admin/report';
import { SimpleModal } from '../../../../components/SimpleModal/SimpleModal';
import { SchoolFormModal } from './SchoolFormModal/SchoolFormModal';
import { addSchoolUnionSchool } from '../../../../helpers/service/adminSU/schoolsTable';
import { KIND } from '../../../../consts/school';
import {
  getTournamentSchoolAreas,
  getSchoolAvailableAges,
  getSchoolAvailableAgesGroup,
  getSchoolAvailableGender
} from 'Src/helpers/accessor/accessor';
import {
  getSelectOptionForAreaSU,
  getSelectOptionForAvailableAges,
  getSelectOptionForAvailableGender,
  getSelectOptionForBooleanQuestion
} from 'Src/helpers/table/select';
import { UnionSchoolClubsReportForm } from './Reports/UnionSchoolClubsReportForm/UnionSchoolClubsReportForm';
import { UnionSchoolFixturesReportForm } from './Reports/UnionSchoolFixturesReportForm/UnionSchoolFixturesReportForm';
import { UnionSchoolTargetCompletionReportForm } from './Reports/UnionSchoolTargetCompletionReportForm/UnionSchoolTargetCompletionReportForm';
import { UnionSchoolTargetParticipationStatisticsReportForm } from './Reports/UnionSchoolTargetParticipationStatisticsReportForm/UnionSchoolTargetParticipationStatisticsReportForm';
import { UnionSchoolTargetCompletionStatisticsReportForm } from './Reports/UnionSchoolTargetCompletionStatisticsReportForm/UnionSchoolTargetCompletionStatisticsReportForm';
import { SchoolTournamentScheduleUnionSideReportForm } from './Reports/SchoolTournamentScheduleUnionSideReportForm/SchoolTournamentScheduleUnionSideReportForm';
import './Schools.css';
import '../Tournaments/Tournaments.css';

const SchoolNameCell = (school: any): React.ReactNode => {
  const name = String(propz.get(school, ['name'], '') || 'Unnamed school');
  const pic = String(propz.get(school, ['pic'], '') || '');
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
  return (
    <td>
      <div className="unionSchoolNameCell">
        <span className="unionSchoolLogo" aria-hidden="true">
          <b>{initials || 'S'}</b>
          {pic ? <img src={pic} alt="" onError={event => { event.currentTarget.style.display = 'none'; }} /> : null}
        </span>
        <strong>{name}</strong>
      </div>
    </td>
  );
};

const UnionWebsiteVisibilityCell = (school: any): React.ReactNode => (
  <td><span className={`unionSchoolWebsiteStatus ${school.isShowOnUnionWebsite === false ? 'is-hidden' : 'is-visible'}`}>{school.isShowOnUnionWebsite === false ? 'Hidden' : 'Shown'}</span></td>
);

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'School',
    field: 'name',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    cell: SchoolNameCell
  },
  {
    text: 'Available ages',
    field: 'availableAges',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getSchoolAvailableAgesGroup
  },
  {
    text: 'Available gender',
    field: 'availableGender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getSchoolAvailableGender
  },
  {
    text: 'Postcode',
    field: 'postcode',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: ['postcode', 'postcode']
  },
  {
    text: 'Domain',
    field: 'domain',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: ['domain']
  },
  {
    text: 'Area',
    field: 'areaSU',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getTournamentSchoolAreas
  },
  {
    text: 'Union website',
    field: 'isShowOnUnionWebsite',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: UnionWebsiteVisibilityCell
  },
  {
    text: 'Postcode',
    field: 'postcodeSearch',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['postcode', 'postcode']
  },
  {
    text: 'Domain',
    field: 'domainSearch',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['domain']
  }
];

const getSchoolsWhere = (filters: any): any => {
  const { postcodeSearch, domainSearch, availableGender, ...otherFilters } = filters || {};
  const where = getServerFieldSectionWhere(otherFilters);
  if (String(postcodeSearch || '').trim()) where.postcodeSearch = { like: String(postcodeSearch).trim(), options: 'i' };
  if (String(domainSearch || '').trim()) where.domainSearch = { like: String(domainSearch).trim(), options: 'i' };
  const genders = Array.isArray(availableGender) ? availableGender : availableGender ? [availableGender] : [];
  if (genders.length > 0) {
    const serverValues = new Set<string>();
    genders.forEach(gender => {
      const genderWhere = getServerFieldSectionWhere({ availableGender: gender });
      const values = propz.get<any[]>(genderWhere, ['availableGender', '$in'], []);
      values.forEach(value => serverValues.add(value));
    });
    where.availableGender = { $in: Array.from(serverValues) };
  }
  return where;
};

interface State {
  items: any[];
  upcomingTournaments: UpcomingTournament[];
  currentPage: number;
  selectedItems: any[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  isSchoolFormOpen: boolean;
  isReportsMenuOpen: boolean;
  isUnionSchoolClubsReportModalOpen: boolean;
  isUnionSchoolFixturesReportModalOpen: boolean;
  isUnionSchoolTargetCompletionReportModalOpen: boolean;
  isUnionSchoolTargetParticipationStatisticsReportModalOpen: boolean;
  isUnionSchoolTargetCompletionStatisticsReportModalOpen: boolean;
  isSchoolTournamentScheduleUnionSideReportModalOpen: boolean;
  errorMessage: string;
  isErrorModalOpen: boolean;

  currentTabIndex: number;
  admins: any[];
  students: any[];
  forms: any[];

  schoolUnions: any[];
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Schools extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      upcomingTournaments: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      isSchoolFormOpen: false,
      isReportsMenuOpen: false,
      isUnionSchoolClubsReportModalOpen: false,
      isUnionSchoolFixturesReportModalOpen: false,
      isUnionSchoolTargetCompletionReportModalOpen: false,
      isUnionSchoolTargetParticipationStatisticsReportModalOpen: false,
      isUnionSchoolTargetCompletionStatisticsReportModalOpen: false,
      isSchoolTournamentScheduleUnionSideReportModalOpen: false,
      errorMessage: '',
      isErrorModalOpen: false,

      currentTabIndex: 0,
      admins: [],
      students: [],
      forms: [],

      schoolUnions: []
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems()
      .catch(error => {
        console.error('Error during data fetching in componentDidMount:', error);
        this.setState({ isLoading: false });
      });
    this.setAdditionalItems().catch(error => console.error('Error fetching school areas:', error));
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps);
    }
  }

  setAdditionalItems() {
    const { user } = this.props;

    return BPromise.all([getAllSchoolUnionSchools(user, { kind: { $in: [KIND.SCHOOL_UNION] } })]).then(
      ([schoolUnions]) => {
        this.setState(state => ({
          schoolUnions: schoolUnions,
          items: state.items.map(item => ({
            ...item,
            areaSU: schoolUnions
              .filter(
                union => Array.isArray(union.schoolIds) && union.schoolIds.some(id => id.toString() === item.id.toString())
              )
              .map(union => ({ name: union.name }))
          }))
        }));

        return true;
      }
    );
  }

  setItems(props: Props = this.props) {
    const { user, location } = props;

    const search = parse(location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const where = getSchoolsWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getItemsPromise = getSchoolUnionSchools(user, serverQueryFilter);
    const getItemsCountPromise = getSchoolUnionSchoolsCount(user, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return BPromise.all(promises).then(([countObj, items]) => {
      const { schoolUnions } = this.state;

      const dataItems = items.map(item => {
        const { id: schoolId } = item;

        const areaSU = schoolUnions
          .filter(
            union => Array.isArray(union.schoolIds) && union.schoolIds.some(id => id.toString() === schoolId.toString())
          )
          .map(union => ({ name: union.name }));

        return {
          ...item,
          areaSU
        };
      });

      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: dataItems,
        total: countObj.count,
        basePath: location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
      currentTabIndex: 0 //  required to display the tab with new data for upcoming tournaments.
    }); //  I don't see any other solution yet
  };

  onSchoolRowClick = (index: number): void => {
    const school = this.state.items[index];
    if (!school) return;
    this.props.history.push({
      pathname: '/schools/manage/overview',
      search: `school=${school.id}`,
      state: { prevPath: this.state.basePath, search: this.props.location.search }
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getSchoolsWhere(filters);

    getAllSchoolUnionSchools(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (options && options.modernMultiValue) {
      nextFilters = { ...nextFilters, [filterField]: Array.isArray(filterValue) ? filterValue : [] };
    } else if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onClearIndividualFilter = (filterField: string): void => {
    const { filters, sortDirection, sortColumnsName, basePath } = this.state;
    const nextFilters = { ...filters };
    delete nextFilters[filterField];
    const search: string[] = [];
    if (isSortExist(sortDirection, sortColumnsName)) search.push(getSearchOrder(sortDirection, sortColumnsName));
    if (isFilterExist2(nextFilters)) search.push(getSearchFilter(nextFilters));
    this.setState({ filters: nextFilters, selectedItems: [], isSelectAllChecked: false });
    this.props.history.push({ pathname: basePath, search: search.join('&') });
  };

  onCancelFilterClick = (): void => {
    this.setState({ isShowFilter: false }, () => this.setItems());
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  onDeleteSchoolClick = () => {
    const { selectedItems } = this.state;
    const user = this.props.user;
    const itemIds = selectedItems.map(item => item.id);

    this.setState({
      isLoading: true
    });

    BPromise.all(itemIds.map(itemId => deleteSchoolUnionSchool(user, itemId))).then(res => {
      this.setState({
        selectedItems: [],
        isSelectAllChecked: false
      });
      this.setItems();
    });
  };

  setSelectedSchoolsWebsiteVisibility = (isVisible: boolean) => {
    const { selectedItems } = this.state;
    this.setState({ isLoading: true });
    setSchoolsUnionWebsiteVisibility(this.props.user, selectedItems.map(item => item.id), isVisible)
      .then(() => this.setState({ selectedItems: [], isSelectAllChecked: false }, () => this.setItems()))
      .catch(() => this.setState({ isLoading: false }));
  };

  onCancelButtonClick = (): void => {
    this.setState({
      isSchoolFormOpen: false
    });
  };

  onAddSchoolButtonClick = (): void => {
    this.setState({
      isSchoolFormOpen: true
    });
  };

  onSaveButtonClick = (data): void => {
    const { user } = this.props;

    this.setState({
      isLoading: true
    });

    addSchoolUnionSchool(user, data).then(school => {
      this.setState({
        isLoading: false,
        isSchoolFormOpen: false
      });
      this.setItems();
    });
  };

  onManageStudentsClick = () => {
    const { user } = this.props;
    const selectedItems = this.state.selectedItems;
    const school = selectedItems[0];
    const schoolId = school.id;

    this.setState({ isLoading: true });

    getSchoolUnionSchoolStudents(user, schoolId)
      .then(students => {
        this.setState({
          currentTabIndex: 3,
          students,
          isLoading: false
        });

        this.props.history.push({
          pathname: `${this.state.basePath}/students`,
          search: `school=${schoolId}`
        });
      })
      .catch(error => {
        console.error('Failed to fetch students:', error);
        this.setState({ isLoading: false });
      });
  };

  onManageFormsClick = () => {
    const { user } = this.props;
    const { selectedItems, filters, currentPage, sortDirection, sortColumnsName } = this.state;
    const school = selectedItems[0];
    const schoolId = school.id;

    // Save only the necessary state
    sessionStorage.setItem(
      'schoolsState',
      JSON.stringify({
        filters,
        currentPage,
        sortDirection,
        sortColumnsName,
        selectedSchool: school // Save selected school object
      })
    );

    this.setState({ isLoading: true });

    getSchoolUnionSchoolForms(user, schoolId)
      .then(forms => {
        this.setState({
          currentTabIndex: 4,
          forms,
          isLoading: false
        });

        this.props.history.push({
          pathname: `${this.state.basePath}/forms`,
          search: `school=${schoolId}`
        });
      })
      .catch(error => {
        console.error('Failed to fetch forms:', error);
        this.setState({ isLoading: false });
      });
  };

  renderSchoolForm() {
    const { isSchoolFormOpen } = this.state;
    const { user } = this.props;
    const schools = this.state.items;

    return (
      <SimpleModal
        isOpen={isSchoolFormOpen}
        title="Add school"
        customClass="unionAddSchoolDialog"
        dialogStyle={{ maxWidth: 560 }}
        onCloseClick={this.onCancelButtonClick}
        showFooter={false}
        isBodyBold={false}
        bodyClassName="unionAddSchoolModalBody"
      >
        <SchoolFormModal
          user={user}
          schools={schools}
          onCancel={this.onCancelButtonClick}
          onSubmit={this.onSaveButtonClick}
        />
      </SimpleModal>
    );
  }

  onSubmitReportForm = (data, reportType) => {
    const { user } = this.props;
    const { activeSchoolId, activeSchool } = user;
    const selectedItems = this.state.selectedItems;
    const school = selectedItems[0];
    const schoolId = propz.get(school, ['id'], '');

    const ageGroup = AGE_GROUPS['ENGLISH'];

    const {
      dateTo,
      dateFrom,
      ageGroups,
      genders,
      isShowInactive,
      isShowBlank,
      isGroupBySports,
      isShowCompleted,
      isShowUncompleted,
      isPrintFullNames,
      isPrintEligible,
      isPrintParticipating,
      isPrintPercent,
      isPrintPP,
      isPrintSEN,
      isPrintFSM,
      isShowBooked,
      isShowWaitingList,
      isShowRemoved,
      isShowOtherUnionTournaments
    } = data;

    let modData;
    let formsUniq;
    let ages: number[];

    REPORT_TYPE_SERVER_TO_CLIENT_MAPPING;

    switch (reportType) {
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.UNION_SCHOOL_CLUBS:
        this.setState({
          isUnionSchoolClubsReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);

        ages = ageGroups.filter(age => age.isChecked).map(age => ageGroup.indexOf(age.name));

        modData = {
          reportParamsCSV: {
            unionId: activeSchoolId,
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            ageGroups: ages,
            genders: genders.filter(gender => gender.isChecked === true).map(gender => gender.name.toUpperCase()),
            showInactive: isShowInactive,
            showBlank: isShowBlank
          }
        };
        break;

      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.UNION_SCHOOL_FIXTURES:
        this.setState({
          isUnionSchoolFixturesReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);

        ages = ageGroups.filter(age => age.isChecked).map(age => ageGroup.indexOf(age.name));

        modData = {
          reportParamsCSV: {
            unionId: activeSchoolId,
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            ageGroups: ages,
            genders: genders.filter(gender => gender.isChecked === true).map(gender => gender.name.toUpperCase()),
            showBlank: isShowBlank,
            groupBySports: isGroupBySports
          }
        };
        break;

      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.UNION_SCHOOL_TARGET_COMPLETION:
        this.setState({
          isUnionSchoolTargetCompletionReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);

        ages = ageGroups.filter(age => age.isChecked).map(age => ageGroup.indexOf(age.name));

        modData = {
          reportParamsCSV: {
            unionId: activeSchoolId,
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            ageGroups: ages,
            genders: genders.filter(gender => gender.isChecked === true).map(gender => gender.name.toUpperCase()),
            showCompleted: isShowCompleted,
            showUncompleted: isShowUncompleted,
            showBlank: isShowBlank,
            printFullNames: isPrintFullNames
          }
        };
        break;

      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.UNION_SCHOOL_TARGET_PARTICIPATION_STATISTICS:
        this.setState({
          isUnionSchoolTargetParticipationStatisticsReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);

        ages = ageGroups.filter(age => age.isChecked).map(age => ageGroup.indexOf(age.name));

        modData = {
          reportParamsCSV: {
            unionId: activeSchoolId,
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            ageGroups: ages,
            genders: genders.filter(gender => gender.isChecked === true).map(gender => gender.name.toUpperCase()),
            printEligible: isPrintEligible,
            printPercent: isPrintPercent,
            printPP: isPrintPP,
            printSEN: isPrintSEN,
            printFSM: isPrintFSM
          }
        };
        break;

      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.UNION_SCHOOL_TARGET_COMPLETION_STATISTICS:
        this.setState({
          isUnionSchoolTargetCompletionStatisticsReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);

        ages = ageGroups.filter(age => age.isChecked).map(age => ageGroup.indexOf(age.name));

        modData = {
          reportParamsCSV: {
            unionId: activeSchoolId,
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            ageGroups: ages,
            genders: genders.filter(gender => gender.isChecked === true).map(gender => gender.name.toUpperCase()),
            printParticipating: isPrintParticipating,
            printPercent: isPrintPercent,
            printPP: isPrintPP,
            printSEN: isPrintSEN,
            printFSM: isPrintFSM
          }
        };
        break;

      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.SCHOOL_TOURNAMENT_SCHEDULE_UNION_SIDE:
        this.setState({
          isSchoolTournamentScheduleUnionSideReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);

        modData = {
          reportParamsCSV: {
            unionId: activeSchoolId,
            schoolId,
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            showBooked: isShowBooked,
            showWaitingList: isShowWaitingList,
            showRemoved: isShowRemoved,
            showOtherUnionTournaments: isShowOtherUnionTournaments
          }
        };
        break;
    }

    uploadReport(user, reportType, modData)
      .then(data => {
        if (typeof data.reportId !== 'undefined') {
          const reportType = data.reportType;

          this.setState({
            isLoading: false
          });

          const url = `${window.apiBase}/public/report/${data.reportId}/reportType/${reportType}`;
          window.open(url);
        } else {
          this.setState({
            isLoading: false,
            errorMessage: 'No read report',
            isErrorModalOpen: true
          });
        }
      })
      .catch(error => {
        const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
        console.error(error);

        this.setState({
          isLoading: false,
          errorMessage: errorText,
          isErrorModalOpen: true
        });
      });
  };

  renderSchoolReportParametersTitle = (title: string): React.ReactNode => (
    <div className="clubsReportModalTitle">
      <span className="clubsReportModalTitle__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5" />
        </svg>
      </span>
      <span>
        <strong>{title}</strong>
        <small>Schools report</small>
      </span>
    </div>
  );

  renderSchoolReportParametersModal = (
    isOpen: boolean,
    title: string,
    onCloseClick: () => void,
    content: React.ReactNode
  ): React.ReactNode => (
    <SimpleModal
      customClass="clubsReportParametersModal eEventReportParametersModal"
      bodyClassName="clubsReportParametersModal__body eEventReportParametersModal__body"
      title={this.renderSchoolReportParametersTitle(title)}
      isOpen={isOpen}
      onCloseClick={onCloseClick}
      showFooter={false}
      isBodyBold={false}
    >
      {content}
    </SimpleModal>
  );

  renderUnionSchoolClubsReportFormModal() {
    const { user } = this.props;
    const { isUnionSchoolClubsReportModalOpen } = this.state;
    const { activeSchool } = user;

    return this.renderSchoolReportParametersModal(
      isUnionSchoolClubsReportModalOpen,
      'Union-wide Extracurricular Club Directory',
      this.closeUnionSchoolClubsReportForm,
        <UnionSchoolClubsReportForm
          onCloseClick={this.closeUnionSchoolClubsReportForm}
          onSubmit={this.onSubmitReportForm}
        />
    );
  }

  renderUnionSchoolFixturesReportFormModal() {
    const { user } = this.props;
    const { isUnionSchoolFixturesReportModalOpen } = this.state;
    const { activeSchool } = user;

    return this.renderSchoolReportParametersModal(
      isUnionSchoolFixturesReportModalOpen,
      'Union-wide Sport Event Summary',
      this.closeUnionSchoolFixturesReportForm,
        <UnionSchoolFixturesReportForm
          onCloseClick={this.closeUnionSchoolFixturesReportForm}
          onSubmit={this.onSubmitReportForm}
        />
    );
  }

  renderUnionSchoolTargetCompletionReportFormModal() {
    const { user } = this.props;
    const { isUnionSchoolTargetCompletionReportModalOpen } = this.state;
    const { activeSchool } = user;

    return this.renderSchoolReportParametersModal(
      isUnionSchoolTargetCompletionReportModalOpen,
      'Union-wide Target Completion Summary',
      this.closeUnionSchoolTargetCompletionReportForm,
        <UnionSchoolTargetCompletionReportForm
          onCloseClick={this.closeUnionSchoolTargetCompletionReportForm}
          onSubmit={this.onSubmitReportForm}
        />
    );
  }

  renderUnionSchoolTargetParticipationStatisticsReportFormModal() {
    const { user } = this.props;
    const { isUnionSchoolTargetParticipationStatisticsReportModalOpen } = this.state;
    const { activeSchool } = user;

    return this.renderSchoolReportParametersModal(
      isUnionSchoolTargetParticipationStatisticsReportModalOpen,
      'Union-wide Target Participation Statistics',
      this.closeUnionSchoolTargetParticipationStatisticsReportForm,
        <UnionSchoolTargetParticipationStatisticsReportForm
          onCloseClick={this.closeUnionSchoolTargetParticipationStatisticsReportForm}
          onSubmit={this.onSubmitReportForm}
        />
    );
  }

  renderUnionSchoolTargetCompletionStatisticsReportFormModal() {
    const { user } = this.props;
    const { isUnionSchoolTargetCompletionStatisticsReportModalOpen } = this.state;
    const { activeSchool } = user;

    return this.renderSchoolReportParametersModal(
      isUnionSchoolTargetCompletionStatisticsReportModalOpen,
      'Union-wide Target Completion Statistics',
      this.closeUnionSchoolTargetCompletionStatisticsReportForm,
        <UnionSchoolTargetCompletionStatisticsReportForm
          onCloseClick={this.closeUnionSchoolTargetCompletionStatisticsReportForm}
          onSubmit={this.onSubmitReportForm}
        />
    );
  }

  renderSchoolTournamentScheduleUnionSideReportFormModal() {
    const { user } = this.props;
    const { isSchoolTournamentScheduleUnionSideReportModalOpen, selectedItems } = this.state;
    const { activeSchool } = user;
    const school = selectedItems[0];

    return this.renderSchoolReportParametersModal(
      isSchoolTournamentScheduleUnionSideReportModalOpen,
      'School tournament schedule',
      this.closeSchoolTournamentScheduleUnionSideReportForm,
        <SchoolTournamentScheduleUnionSideReportForm
          onCloseClick={this.closeSchoolTournamentScheduleUnionSideReportForm}
          onSubmit={this.onSubmitReportForm}
          school={school}
        />
    );
  }

  openUnionSchoolClubsReportForm = () => {
    this.setState({
      isUnionSchoolClubsReportModalOpen: true
    });
  };

  closeUnionSchoolClubsReportForm = () => {
    this.setState({
      isUnionSchoolClubsReportModalOpen: false
    });
  };

  openUnionSchoolFixturesReportForm = () => {
    this.setState({
      isUnionSchoolFixturesReportModalOpen: true
    });
  };

  closeUnionSchoolFixturesReportForm = () => {
    this.setState({
      isUnionSchoolFixturesReportModalOpen: false
    });
  };

  openUnionSchoolTargetCompletionReportForm = () => {
    this.setState({
      isUnionSchoolTargetCompletionReportModalOpen: true
    });
  };

  closeUnionSchoolTargetCompletionReportForm = () => {
    this.setState({
      isUnionSchoolTargetCompletionReportModalOpen: false
    });
  };

  openUnionSchoolTargetParticipationStatisticsReportForm = () => {
    this.setState({
      isUnionSchoolTargetParticipationStatisticsReportModalOpen: true
    });
  };

  closeUnionSchoolTargetParticipationStatisticsReportForm = () => {
    this.setState({
      isUnionSchoolTargetParticipationStatisticsReportModalOpen: false
    });
  };

  openUnionSchoolTargetCompletionStatisticsReportForm = () => {
    this.setState({
      isUnionSchoolTargetCompletionStatisticsReportModalOpen: true
    });
  };

  closeUnionSchoolTargetCompletionStatisticsReportForm = () => {
    this.setState({
      isUnionSchoolTargetCompletionStatisticsReportModalOpen: false
    });
  };

  openSchoolTournamentScheduleUnionSideReportForm = () => {
    this.setState({
      isSchoolTournamentScheduleUnionSideReportModalOpen: true
    });
  };

  closeSchoolTournamentScheduleUnionSideReportForm = () => {
    this.setState({
      isSchoolTournamentScheduleUnionSideReportModalOpen: false
    });
  };

  renderGrid() {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
      schoolUnions
    } = this.state;

    const actionItems = [
      selectedItems.length >= 1 && {
        itemText: 'Show on union website',
        onItemClick: () => this.setSelectedSchoolsWebsiteVisibility(true),
        isActive: true
      },
      selectedItems.length >= 1 && {
        itemText: 'Hide from union website',
        onItemClick: () => this.setSelectedSchoolsWebsiteVisibility(false),
        isActive: true
      },
      selectedItems.length >= 1 && {
        itemText: selectedItems.length === 1 ? 'Delete school' : 'Delete schools',
        onItemClick: this.onDeleteSchoolClick,
        isActive: true
      },
    ].filter(Boolean);

    const filterOptions = {
      availableAges: getSelectOptionForAvailableAges(),
      availableGender: getSelectOptionForAvailableGender(),
      areaSU: getSelectOptionForAreaSU(schoolUnions)
    };

    const dataItems = items.map(item => {
      const { id: schoolId } = item;

      schoolUnions.forEach(union => {});

      const areaSU = schoolUnions
        .filter(union => {
          const { schoolIds } = union;
          if (!Array.isArray(schoolIds)) {
            console.error(`SchoolIds is not an array for union: ${union.name}`);
            return false;
          }

          const match = schoolIds.some(id => id?.toString() === schoolId?.toString());
          return match;
        })
        .map(union => ({ name: union.name }));

      return {
        ...item,
        areaSU
      };
    });

    return (
      <Grid2
        dataItems={dataItems}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        actionItems={actionItems}
        isCSV={false}
        options={filterOptions}
        onItemClick={this.onSchoolRowClick}
        onItemSelect={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onClearIndividualFilter={this.onClearIndividualFilter}
        onCancelFilterClick={this.onCancelFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
        user={this.props.user}
        gridTitle={`${total} schools`}
        toolbarSearchField="name"
        toolbarSearchPlaceholder="Search schools..."
        onToolbarSearchClear={() => this.onClearIndividualFilter('name')}
        isActionsInline={true}
        selectionActionsBelow={true}
        hiddenFilterFields={['name']}
        columnVisibilityKey="union.schools.list"
        fixedColumnFields={['name']}
        filterOnlyFields={['postcodeSearch', 'domainSearch']}
        emptyIcon={faSchool}
        emptyText="No schools yet"
        emptyHint="Member schools will appear here when they are added to this union."
        emptyFilteredText="No schools match your filters"
        emptyFilteredHint="Try changing or clearing the search and filters to see more schools."
        renderAdditionalOptions={() => this.renderReportsMenu()}
      />
    );
  }

  getSummaryTabs(): React.ReactNode {
    const { currentTabIndex, selectedItems, upcomingTournaments, admins } = this.state;

    const school = selectedItems[0];
    switch (currentTabIndex) {
      case 0:
        return <SchoolSummary school={school} />;
      case 1:
        return <SchoolUpcomingTournamentsSummary upcomingTournaments={upcomingTournaments} />;
      case 2:
        return <SchoolAdminsSummary admins={admins} />;
      case 3:
        return this.renderStudentSummary();
      case 4:
        return this.renderFormSummary();
      default:
        return null;
    }
  }

  onTabClick = (event, index: number) => {
    event.preventDefault();
    const { user } = this.props;
    const { selectedItems } = this.state;
    const school = selectedItems[0];
    const memberSchoolId = school.id;

    switch (index) {
      case 1:
        this.setState({
          isLoading: true
        });
        getUpcomingTournaments(user, memberSchoolId).then(upcomingTournaments => {
          this.setState({
            currentTabIndex: index,
            upcomingTournaments: upcomingTournaments,
            isLoading: false
          });
        });
        break;
      case 2:
        this.setState({
          isLoading: true
        });
        getAllSchoolAdmins(user, memberSchoolId).then(admins => {
          this.setState({
            currentTabIndex: index,
            admins: admins,
            isLoading: false
          });
        });
        break;
      case 3:
        this.setState({
          isLoading: true
        });
        getSchoolUnionSchoolStudents(user, memberSchoolId).then(students => {
          this.setState({
            currentTabIndex: index,
            students: students,
            isLoading: false
          });
        });
        break;
      case 4:
        this.setState({
          isLoading: true
        });
        getSchoolUnionSchoolForms(user, memberSchoolId).then(forms => {
          this.setState({
            currentTabIndex: index,
            forms: forms,
            isLoading: false
          });
        });
        break;
      default:
        this.setState({
          currentTabIndex: index
        });
    }
  };

  onShowAdminsButtonClick = (): void => {
    const selectedItems = this.state.selectedItems;
    const school = selectedItems[0];
    const schoolId = school.id;

    this.props.history.push({
      pathname: `${this.state.basePath}/admins`,
      search: `school=${schoolId}`
    });
  };

  renderSummary(): React.ReactNode {
    const { currentTabIndex } = this.state;

    const isOneItemSelected = this.isOneItemSelected();

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select a school above</div>;
    }

    const summaryTabs = this.getSummaryTabs();

    const isShowAdminsButton = currentTabIndex === 2;

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={SCHOOL_TABS} currentTabIndex={currentTabIndex}>
        {isShowAdminsButton && (
          <Button onClick={this.onShowAdminsButtonClick} text={'Show admins'} customClass={'mt-3 mb-3 mr-3'} />
        )}
        {summaryTabs}
      </GenericSummary>
    );
  }

  renderReportsMenu(): React.ReactNode {
    const { isReportsMenuOpen, selectedItems } = this.state;
    const reports = selectedItems.length === 1
      ? [{ label: 'School Tournament Schedule', description: 'Tournament bookings, waiting-list places and removals for the selected school.', action: this.openSchoolTournamentScheduleUnionSideReportForm }]
      : selectedItems.length === 0
      ? [
          { label: 'Extracurricular Club Directory', description: 'Clubs offered across all schools in the union.', action: this.openUnionSchoolClubsReportForm },
          { label: 'Sport Event Summary', description: 'A union-wide summary of school fixtures and sporting events.', action: this.openUnionSchoolFixturesReportForm },
          { label: 'Aggregated Target Completion', description: 'Review target completion across member schools.', action: this.openUnionSchoolTargetCompletionReportForm },
          { label: 'Target Participation Statistics', description: 'Compare participation against configured targets.', action: this.openUnionSchoolTargetParticipationStatisticsReportForm },
          { label: 'Target Completion Statistics', description: 'Detailed completion statistics for union targets.', action: this.openUnionSchoolTargetCompletionStatisticsReportForm }
        ]
      : [];
    if (reports.length === 0) return null;
    return (
      <div className="unionSchoolsReports">
        <button type="button" className="unionTournamentsReportsButton" onClick={() => this.setState({ isReportsMenuOpen: true })}>Reports</button>
        <SimpleModal customClass="eEventPrintModal unionTournamentReportsModal" isOpen={isReportsMenuOpen} title="Printed forms & reports" onCloseClick={() => this.setState({ isReportsMenuOpen: false })} showFooter={false} isBodyBold={false}>
          <div className="eEventPrintModal__intro">Choose a report. You can configure its contents and format on the next step.</div>
          <section className="eEventPrintModal__group">
            <h3 className="eEventPrintModal__groupTitle">{selectedItems.length === 1 ? 'Selected school reports' : 'Union reports'}</h3>
            <div className="eEventPrintModal__list">
              {reports.map(report => (
                <button type="button" className="eEventPrintModal__item" key={report.label} onClick={() => this.setState({ isReportsMenuOpen: false }, report.action)}>
                  <span className="eEventPrintModal__itemIcon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5" /></svg></span>
                  <span className="eEventPrintModal__itemCopy"><span className="eEventPrintModal__itemHeading"><span>{report.label}</span><span className="eEventPrintModal__format">Excel</span></span><span className="eEventPrintModal__itemDescription">{report.description}</span></span>
                  <span className="eEventPrintModal__arrow" aria-hidden="true">›</span>
                </button>
              ))}
            </div>
          </section>
        </SimpleModal>
      </div>
    );
  }

  closeSchoolDrawer = (): void => {
    this.setState({ selectedItems: [], currentTabIndex: 0, isSelectAllChecked: false });
  };

  renderSchoolDrawer(): React.ReactNode {
    const { selectedItems } = this.state;
    if (selectedItems.length !== 1) return null;

    const school = selectedItems[0];
    const name = propz.get(school, ['name'], 'School');
    const postcode = propz.get(school, ['postcode', 'postcode'], 'Postcode not set');

    return (
      <>
        <div className="unionSchoolsDrawerScrim" onClick={this.closeSchoolDrawer} />
        <aside className="unionSchoolsDrawer" role="dialog" aria-modal="true" aria-label={`${name} details`}>
          <div className="unionSchoolsDrawer__head">
            <div>
              <span className="unionSchoolsDrawer__eyebrow">School details</span>
              <h2>{name}</h2>
              <p>{postcode}</p>
            </div>
            <button type="button" onClick={this.closeSchoolDrawer} aria-label="Close school details">×</button>
          </div>
          <div className="unionSchoolsDrawer__body">{this.renderSummary()}</div>
        </aside>
      </>
    );
  }

  renderStudentSummary(): React.ReactNode {
    return (
      <div>
        <Button onClick={this.onManageStudentsClick} text={'Manage Students'} customClass={'mt-3 mb-3'} />
      </div>
    );
  }

  renderFormSummary(): React.ReactNode {
    return (
      <div>
        <Button onClick={this.onManageFormsClick} text={'Manage Forms'} customClass={'mt-3 mb-3'} />
      </div>
    );
  }

  render() {
    const { isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="unionSchoolsPage unionTournamentsModern unionTournamentsListShell">
        {this.renderSchoolForm()}
        {this.renderUnionSchoolClubsReportFormModal()}
        {this.renderUnionSchoolFixturesReportFormModal()}
        {this.renderUnionSchoolTargetCompletionReportFormModal()}
        {this.renderUnionSchoolTargetParticipationStatisticsReportFormModal()}
        {this.renderUnionSchoolTargetCompletionStatisticsReportFormModal()}
        {this.renderSchoolTournamentScheduleUnionSideReportFormModal()}
        <header className="unionSchoolsPageHeader">
          <div className="unionSchoolsPageHeader__title">
            <h1>Schools</h1>
            <span>{this.state.total}</span>
          </div>
          <button type="button" className="unionSchoolsPrimaryAction" onClick={this.onAddSchoolButtonClick}>
            <span aria-hidden="true">+</span> Add school
          </button>
        </header>
        <main className="unionSchoolsListCard unionTournamentsListCard">{this.renderGrid()}</main>
      </div>
    );
  }
}




